import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Phone, Mail, LogOut, RefreshCw } from "lucide-react";
import { DnsVerificationChecklist } from "@/components/admin/DnsVerificationChecklist";

type Status = "new" | "contacted" | "booked" | "closed";

interface QuoteRequest {
  id: string;
  name: string;
  email: string;
  phone: string;
  pickup_location: string;
  dropoff_location: string | null;
  pickup_datetime: string | null;
  passengers: number | null;
  occasion: string | null;
  notes: string | null;
  status: Status;
  admin_notes: string | null;
  created_at: string;
}

const STATUS_COLORS: Record<Status, string> = {
  new: "bg-purple-100 text-purple-800 border-purple-300",
  contacted: "bg-blue-100 text-blue-800 border-blue-300",
  booked: "bg-green-100 text-green-800 border-green-300",
  closed: "bg-gray-100 text-gray-700 border-gray-300",
};

export const Route = createFileRoute("/_admin/admin")({
  component: AdminDashboard,
});

function AdminDashboard() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState<QuoteRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Status | "all">("all");
  const [selected, setSelected] = useState<QuoteRequest | null>(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("quote_requests")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) toast.error(error.message);
    setQuotes((data ?? []) as QuoteRequest[]);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id: string, status: Status) => {
    const { error } = await supabase.from("quote_requests").update({ status }).eq("id", id);
    if (error) { toast.error(error.message); return; }
    setQuotes((q) => q.map((x) => (x.id === id ? { ...x, status } : x)));
    if (selected?.id === id) setSelected({ ...selected, status });
    toast.success("Status updated");
  };

  const saveNotes = async (id: string, admin_notes: string) => {
    const { error } = await supabase.from("quote_requests").update({ admin_notes }).eq("id", id);
    if (error) { toast.error(error.message); return; }
    toast.success("Notes saved");
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    navigate({ to: "/login" });
  };

  const visible = filter === "all" ? quotes : quotes.filter((q) => q.status === filter);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <Link to="/" className="text-xs text-muted-foreground tracking-[0.2em] uppercase">Atlanta Luxury Sprinters</Link>
            <h1 className="font-heading text-xl font-semibold">Quote Requests</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin/leads" className="text-sm font-medium text-primary hover:underline">Leads</Link>
            <button onClick={load} className="text-sm flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button onClick={signOut} className="text-sm flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <DnsVerificationChecklist />
        <div className="flex flex-wrap gap-2 mb-6">
          {(["all", "new", "contacted", "booked", "closed"] as const).map((s) => {
            const count = s === "all" ? quotes.length : quotes.filter((q) => q.status === s).length;
            return (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={`px-4 py-2 rounded-md text-sm border transition-colors ${
                  filter === s
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-card border-border hover:border-primary"
                }`}
              >
                {s[0].toUpperCase() + s.slice(1)} ({count})
              </button>
            );
          })}
        </div>

        {loading ? (
          <p className="text-muted-foreground">Loading…</p>
        ) : visible.length === 0 ? (
          <div className="luxury-card rounded-lg p-12 text-center">
            <p className="text-muted-foreground">No quote requests yet.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {visible.map((q) => (
              <button
                key={q.id}
                onClick={() => setSelected(q)}
                className="w-full text-left luxury-card rounded-lg p-4 hover:border-primary transition-colors"
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-semibold text-foreground">{q.name}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_COLORS[q.status]}`}>
                        {q.status}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {q.pickup_location}{q.dropoff_location ? ` → ${q.dropoff_location}` : ""}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {q.email} · {q.phone}
                      {q.pickup_datetime ? ` · ${new Date(q.pickup_datetime).toLocaleString()}` : ""}
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground whitespace-nowrap">
                    {new Date(q.created_at).toLocaleString()}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <QuoteDetail
          quote={selected}
          onClose={() => setSelected(null)}
          onStatus={(s) => setStatus(selected.id, s)}
          onSaveNotes={(n) => saveNotes(selected.id, n)}
        />
      )}
    </div>
  );
}

function QuoteDetail({
  quote, onClose, onStatus, onSaveNotes,
}: {
  quote: QuoteRequest;
  onClose: () => void;
  onStatus: (s: Status) => void;
  onSaveNotes: (n: string) => void;
}) {
  const [notes, setNotes] = useState(quote.admin_notes ?? "");
  useEffect(() => setNotes(quote.admin_notes ?? ""), [quote.id, quote.admin_notes]);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-card w-full sm:max-w-2xl sm:rounded-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="font-heading text-xl font-semibold">{quote.name}</h2>
            <p className="text-xs text-muted-foreground">{new Date(quote.created_at).toLocaleString()}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-2xl leading-none">×</button>
        </div>
        <div className="p-6 space-y-5">
          <div className="flex flex-wrap gap-2">
            <a href={`tel:${quote.phone}`} className="btn-primary rounded-md inline-flex items-center gap-2 text-sm">
              <Phone className="w-4 h-4" /> Call
            </a>
            <a href={`mailto:${quote.email}?subject=Your%20Atlanta%20Luxury%20Sprinters%20Quote`} className="btn-outline rounded-md inline-flex items-center gap-2 text-sm">
              <Mail className="w-4 h-4" /> Email
            </a>
          </div>

          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <Field label="Email" value={quote.email} />
            <Field label="Phone" value={quote.phone} />
            <Field label="Pickup" value={quote.pickup_location} />
            <Field label="Drop-off" value={quote.dropoff_location ?? "—"} />
            <Field label="Date/Time" value={quote.pickup_datetime ? new Date(quote.pickup_datetime).toLocaleString() : "—"} />
            <Field label="Passengers" value={quote.passengers?.toString() ?? "—"} />
            <Field label="Service / Occasion" value={quote.occasion ?? "—"} />
          </div>

          {quote.notes && (
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">Customer Details</p>
              <pre className="whitespace-pre-wrap text-sm bg-muted/40 p-3 rounded-md font-sans">{quote.notes}</pre>
            </div>
          )}

          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">Status</p>
            <div className="flex flex-wrap gap-2">
              {(["new", "contacted", "booked", "closed"] as const).map((s) => (
                <button key={s} onClick={() => onStatus(s)}
                  className={`px-3 py-1.5 rounded-md text-xs border ${
                    quote.status === s ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"
                  }`}>{s}</button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">Internal Notes</p>
            <textarea
              value={notes} onChange={(e) => setNotes(e.target.value)}
              rows={4}
              className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm"
              placeholder="Notes for your team…"
            />
            <button onClick={() => onSaveNotes(notes)} className="btn-primary rounded-md mt-2 text-sm">
              Save notes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
      <p className="text-foreground">{value}</p>
    </div>
  );
}
